import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import LanguageToggle from '../../components/common/LanguageToggle';
import {
  Shield, Loader2, AlertCircle, Building2,
  Clock, CloudCog, Users, Sun, Moon,
  CheckCircle, KeyRound,
} from 'lucide-react';
import OperisLogo from '../../components/common/OperisLogo';
import AnimatedClock from '../../components/common/AnimatedClock';
import WaveRiver from '../../components/landing/WaveRiver';
import { Eye, EyeOff } from 'lucide-react';
import { requestPasswordReset, resetPassword } from '../../api/auth.api';

const floatingTimes = [
  { text: '08:30', x: 8, y: 18, delay: 0, dur: 7 },
  { text: '14:22', x: 80, y: 14, delay: 2, dur: 9 },
  { text: '17:45', x: 85, y: 75, delay: 4, dur: 8 },
  { text: '23:00', x: 10, y: 80, delay: 1, dur: 6 },
];

/* ── Rotating rings around brand-panel clock ── */
const brandRings = [
  { diameter: 290, color: '#d97706', colorLight: '#d97706', opacity: 0.22, opacityLight: 0.18, dashed: true, speed: 30 },
  { diameter: 330, color: '#f59e0b', colorLight: '#f59e0b', opacity: 0.16, opacityLight: 0.12, dashed: false, speed: 45 },
  { diameter: 370, color: '#ea580c', colorLight: '#b45309', opacity: 0.12, opacityLight: 0.08, dashed: true, speed: 55 },
];

const LoginPage = () => {
  const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation();
  const { loginWithAzure, loginWithCredentials, authError } = useAuth();
  const { darkMode, toggleTheme } = useTheme();

  const features = useMemo(
    () => [
      { icon: Clock, label: t('auth.login.featureRealtime') },
      { icon: CloudCog, label: t('auth.login.featureSapSync') },
      { icon: Users, label: t('auth.login.featureTeam') },
    ],
    [t]
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [confirmResetPassword, setConfirmResetPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [resetToken, setResetToken] = useState('');

  // 2FA state
  const [needs2fa, setNeeds2fa] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);

  useEffect(() => {
    const token = searchParams.get('resetToken');
    const resetEmail = searchParams.get('email');
    if (token) {
      setResetToken(token);
      setAuthMode('reset');
      if (resetEmail) setEmail(resetEmail);
      setPassword('');
      setLocalError('');
      setLocalSuccess('');
    }
  }, [searchParams]);

  /* ── handlers ── */
  const handleAzureLogin = () => {
    setLoading(true);
    setLocalError('');
    try {
      loginWithAzure();
    } catch (error) {
      console.error('Azure AD error:', error);
      setLocalError(t('auth.login.errors.azureFailed'));
      setLoading(false);
    }
  };

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setLocalError(t('auth.login.errors.needEmailPassword')); return; }
    if (needs2fa && twoFactorCode.length !== 6) { setLocalError(t('auth.login.errors.needSixDigit')); return; }
    setLoading(true);
    setLocalError('');
    try {
      const result = await loginWithCredentials(email, password, needs2fa ? twoFactorCode : null, needs2fa ? trustDevice : false);
      if (result?.requiresTwoFactor) {
        setNeeds2fa(true);
        setLoading(false);
        return;
      }
      navigate('/dashboard');
    } catch (error) {
      setLocalError(error.response?.data?.message || t('auth.login.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setLocalError('Please enter your email address'); return; }
    if (!resetPasswordValue || resetPasswordValue.length < 8) {
      setLocalError('Password must be at least 8 characters long.');
      return;
    }
    if (resetPasswordValue !== confirmResetPassword) {
      setLocalError('Passwords do not match.');
      return;
    }
    setLoading(true);
    setLocalError('');
    setLocalSuccess('');
    try {
      const result = await requestPasswordReset(email, resetPasswordValue);
      setLocalSuccess(result?.message || 'Password reset successfully. You can sign in now.');
      setAuthMode('login');
      setPassword('');
      setResetPasswordValue('');
      setConfirmResetPassword('');
      navigate('/login');
    } catch (error) {
      setLocalError(error.response?.data?.message || 'Unable to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPasswordValue || resetPasswordValue.length < 8) {
      setLocalError('Password must be at least 8 characters long.');
      return;
    }
    if (resetPasswordValue !== confirmResetPassword) {
      setLocalError('Passwords do not match.');
      return;
    }
    setLoading(true);
    setLocalError('');
    setLocalSuccess('');
    try {
      const result = await resetPassword(resetToken, resetPasswordValue);
      setLocalSuccess(result?.message || 'Password reset successfully. You can sign in now.');
      setAuthMode('login');
      setPassword('');
      setResetPasswordValue('');
      setConfirmResetPassword('');
      setResetToken('');
      setSearchParams({});
    } catch (error) {
      setLocalError(error.response?.data?.message || 'Unable to reset password. Please request a new link.');
    } finally {
      setLoading(false);
    }
  };

  const showLogin = () => {
    setAuthMode('login');
    setLocalError('');
    setLocalSuccess('');
    setResetPasswordValue('');
    setConfirmResetPassword('');
    setSearchParams({});
  };

  /* ── Theme-dependent tokens ── */
  const bg = darkMode ? 'bg-[#0f0f14]' : 'bg-gray-50';
  const gradientOverlay = darkMode
    ? 'bg-gradient-to-br from-[#0f0f14] via-[#1a1a24] to-[#2a1f0a]/70'
    : 'bg-gradient-to-br from-gray-50 via-white to-amber-50/50';
  const dotColor = darkMode ? 'rgba(217,119,6,0.05)' : 'rgba(245,158,11,0.06)';
  const particleColor = darkMode ? 'text-amber-400' : 'text-amber-500';
  const panelBg = darkMode
    ? 'border-amber-500/[0.12] bg-white/[0.03]'
    : 'border-gray-200/70 bg-white/80 shadow-xl';
  const featureCard = darkMode
    ? 'border-amber-500/[0.1] bg-white/[0.03]'
    : 'border-gray-200 bg-gray-50';
  const featureIcon = darkMode ? 'text-amber-400' : 'text-amber-600';
  const featureLabel = darkMode ? 'text-white/60' : 'text-gray-600';
  const formCardBg = darkMode
    ? 'border-amber-500/[0.12] bg-white/[0.04] backdrop-blur-sm'
    : 'border-gray-200/70 bg-white shadow-xl';
  const headingColor = darkMode ? 'text-white' : 'text-gray-900';
  const subColor = darkMode ? 'text-white/50' : 'text-gray-500';
  const labelColor = darkMode ? 'text-white/60' : 'text-gray-700';
  const inputClass = darkMode
    ? 'border-amber-500/20 bg-white/[0.06] text-white placeholder-white/30 focus:ring-amber-500'
    : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-amber-500';
  const errorBg = darkMode ? 'border-red-500/20 bg-red-500/10' : 'border-red-200 bg-red-50';
  const errorIcon = darkMode ? 'text-red-400' : 'text-red-500';
  const errorText = darkMode ? 'text-red-300' : 'text-red-700';
  const successBg = darkMode ? 'border-green-500/20 bg-green-500/10' : 'border-green-200 bg-green-50';
  const successIcon = darkMode ? 'text-green-400' : 'text-green-500';
  const successText = darkMode ? 'text-green-300' : 'text-green-700';
  const dividerLine = darkMode ? 'border-amber-500/10' : 'border-gray-200';
  const dividerText = darkMode ? 'text-white/40' : 'text-gray-500';
  const linkColor = darkMode ? 'text-white/40' : 'text-gray-600';
  const linkAccent = darkMode ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-700';
  const footerColor = darkMode ? 'text-white/25' : 'text-gray-400';

  return (
    <div className={`relative min-h-screen overflow-hidden transition-colors duration-500 ${bg}`}>
      <style>{`
        input[type="password"]::-ms-reveal,
        input[type="password"]::-ms-clear {
          display: none;
        }
      `}</style>
      {/* ── Background layers ── */}
      <div className={`absolute inset-0 transition-colors duration-500 ${gradientOverlay}`} />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(${dotColor} 1px, transparent 1px)`,
          backgroundSize: '32px 32px',
        }}
      />
      <div
        className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{
          width: 800, height: 800,
          background: darkMode
            ? 'radial-gradient(circle, rgba(217,119,6,0.08) 0%, transparent 55%)'
            : 'radial-gradient(circle, rgba(245,158,11,0.08) 0%, transparent 50%)',
        }}
      />

      {/* ── WaveRiver ── */}
      <WaveRiver darkMode={darkMode} />

      {/* ── Floating time particles ── */}
      {floatingTimes.map((p, i) => (
        <div
          key={i}
          className={`absolute pointer-events-none select-none font-mono text-xs ${particleColor}`}
          style={{ left: `${p.x}%`, top: `${p.y}%`, opacity: 0.06, animation: `float ${p.dur}s ease-in-out ${p.delay}s infinite` }}
        >
          {p.text}
        </div>
      ))}

      {/* ── Content ── */}
      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-6xl flex-col p-5 lg:p-8">
        {/* Theme + language — top-right */}
        <div className="flex justify-end items-center gap-2 pb-2">
          <button
            onClick={toggleTheme}
            className={`rounded-lg p-2 transition-all duration-300 ${darkMode
              ? 'text-white/60 hover:text-white hover:bg-white/10'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
              }`}
            aria-label={t('auth.login.toggleThemeAria')}
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <LanguageToggle className="rounded-lg" />
        </div>

        <div className="grid flex-1 items-center gap-6 lg:grid-cols-2">
          {/* ── Left: brand panel + clock with rings ── */}
          <section className={`hidden flex-col items-center justify-center rounded-3xl border p-10 backdrop-blur-sm lg:flex transition-colors duration-500 ${panelBg}`}>
            <OperisLogo size="lg" light={darkMode} showTagline />

            <div className="relative mt-10 flex items-center justify-center" style={{ minHeight: 280, minWidth: 280 }}>
              {brandRings.map((ring, i) => (
                <div
                  key={i}
                  className="absolute pointer-events-none rounded-full"
                  style={{
                    width: ring.diameter,
                    height: ring.diameter,
                    top: '50%',
                    left: '50%',
                    marginTop: -ring.diameter / 2,
                    marginLeft: -ring.diameter / 2,
                    border: `1px ${ring.dashed ? 'dashed' : 'solid'} ${darkMode ? ring.color : ring.colorLight}`,
                    opacity: darkMode ? ring.opacity : ring.opacityLight,
                    animation: `spin ${ring.speed}s linear infinite`,
                  }}
                />
              ))}
              <AnimatedClock
                size={220}
                compact
                showDigital={false}
                burst
                glowColor={darkMode ? '#d97706' : '#d97706'}
                lightDial={!darkMode}
              />
            </div>

            <div className="mt-10 w-full max-w-xs space-y-3">
              {features.map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.label} className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors duration-500 ${featureCard}`}>
                    <Icon className={`h-4 w-4 flex-shrink-0 ${featureIcon}`} />
                    <span className={`text-sm ${featureLabel}`}>{f.label}</span>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── Right: sign-in form ── */}
          <section className="mx-auto w-full max-w-md animate-slide-up">
            <div className={`rounded-2xl border p-8 shadow-2xl transition-colors duration-500 ${formCardBg}`}>
              <div className="mb-8 text-center lg:text-left">
                {/* Mobile-only logo + clock */}
                <div className="mb-4 lg:hidden">
                  <OperisLogo size="md" light={darkMode} />
                </div>
                <div className="mb-3 flex justify-center lg:hidden">
                  <AnimatedClock
                    size={120}
                    compact
                    showDigital={false}
                    burst
                    glowColor={darkMode ? '#d97706' : '#d97706'}
                    lightDial={!darkMode}
                  />
                </div>
                <h2 className={`text-2xl font-bold ${headingColor}`}>Sign in</h2>
                <p className={`mt-1 text-sm ${subColor}`}>Welcome back to Operis.</p>
              </div>

              {(authError || localError) && (
                <div className={`mb-6 flex items-start gap-3 rounded-lg border p-4 ${errorBg}`}>
                  <AlertCircle className={`mt-0.5 h-5 w-5 flex-shrink-0 ${errorIcon}`} />
                  <p className={`text-sm ${errorText}`}>{authError || localError}</p>
                </div>
              )}

              {localSuccess && (
                <div className={`mb-6 flex items-start gap-3 rounded-lg border p-4 ${successBg}`}>
                  <CheckCircle className={`mt-0.5 h-5 w-5 flex-shrink-0 ${successIcon}`} />
                  <p className={`text-sm ${successText}`}>{localSuccess}</p>
                </div>
              )}

              {/* <button
                onClick={handleAzureLogin}
                disabled={loading}
                className="mb-4 w-full flex items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 py-3.5 px-4 font-semibold text-white shadow-lg shadow-indigo-500/20 transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/30 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Shield className="w-5 h-5" />}
                Sign in with Microsoft Entra ID
              </button> */}

              {/* <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className={`w-full border-t ${dividerLine}`} />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className={`px-4 ${dividerText}`} style={{ backgroundColor: 'inherit' }}>
                    or sign in with credentials
                  </span>
                </div>
              </div> */}

              {authMode === 'forgot' && (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${labelColor}`}>Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all ${inputClass}`}
                      placeholder="you@company.com"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${labelColor}`}>New password</label>
                    <div className="relative">
                      <input
                        type={showResetPassword ? 'text' : 'password'}
                        value={resetPasswordValue}
                        onChange={(e) => setResetPasswordValue(e.target.value)}
                        className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all pr-12 ${inputClass}`}
                        placeholder="New password"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowResetPassword(!showResetPassword)}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${darkMode
                          ? 'text-blue-400 hover:text-blue-300'
                          : 'text-gray-500 hover:text-gray-700'
                          }`}
                        tabIndex="-1"
                      >
                        {showResetPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${labelColor}`}>Confirm password</label>
                    <input
                      type={showResetPassword ? 'text' : 'password'}
                      value={confirmResetPassword}
                      onChange={(e) => setConfirmResetPassword(e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all ${inputClass}`}
                      placeholder="Confirm password"
                      disabled={loading}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full flex items-center justify-center gap-2 rounded-xl py-3.5 px-4 font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${darkMode
                      ? 'border border-white/15 bg-white/[0.06] text-white hover:bg-white/[0.1]'
                      : 'bg-gray-900 text-white hover:bg-gray-800 shadow-lg'
                      }`}
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <KeyRound className="w-5 h-5" />}
                    Reset password
                  </button>
                  <button type="button" onClick={showLogin} className={`w-full text-sm font-semibold ${linkAccent}`}>
                    Back to sign in
                  </button>
                </form>
              )}

              {authMode === 'reset' && (
                <form onSubmit={handleResetPassword} className="space-y-5">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${labelColor}`}>New password</label>
                    <div className="relative">
                      <input
                        type={showResetPassword ? 'text' : 'password'}
                        value={resetPasswordValue}
                        onChange={(e) => setResetPasswordValue(e.target.value)}
                        className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all pr-12 ${inputClass}`}
                        placeholder="New password"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowResetPassword(!showResetPassword)}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${darkMode
                          ? 'text-blue-400 hover:text-blue-300'
                          : 'text-gray-500 hover:text-gray-700'
                          }`}
                        tabIndex="-1"
                      >
                        {showResetPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${labelColor}`}>Confirm password</label>
                    <input
                      type={showResetPassword ? 'text' : 'password'}
                      value={confirmResetPassword}
                      onChange={(e) => setConfirmResetPassword(e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all ${inputClass}`}
                      placeholder="Confirm password"
                      disabled={loading}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full flex items-center justify-center gap-2 rounded-xl py-3.5 px-4 font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${darkMode
                      ? 'border border-white/15 bg-white/[0.06] text-white hover:bg-white/[0.1]'
                      : 'bg-gray-900 text-white hover:bg-gray-800 shadow-lg'
                      }`}
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <KeyRound className="w-5 h-5" />}
                    Reset password
                  </button>
                  <button type="button" onClick={showLogin} className={`w-full text-sm font-semibold ${linkAccent}`}>
                    Back to sign in
                  </button>
                </form>
              )}

              <form onSubmit={handleLocalLogin} className={`space-y-5 ${authMode === 'login' ? '' : 'hidden'}`}>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${labelColor}`}>Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all ${inputClass}`}
                    placeholder={t('auth.login.emailPlaceholder')}
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${labelColor}`}>
                    {t('auth.login.password')}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all pr-12 ${inputClass}`}
                      placeholder="••••••••"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${darkMode
                        ? 'text-blue-400 hover:text-blue-300'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                      tabIndex="-1"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => { setAuthMode('forgot'); setLocalError(''); setLocalSuccess(''); }}
                      className={`text-sm font-semibold ${linkAccent}`}
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>

                {/* 2FA Code Input — shown after password is verified */}
                {needs2fa && (
                  <div className="animate-slide-up">
                    <div className={`flex items-start gap-3 rounded-lg border p-3 mb-3 ${darkMode ? 'border-amber-500/20 bg-amber-500/10' : 'border-amber-200 bg-amber-50'}`}>
                      <Shield className={`mt-0.5 h-4 w-4 flex-shrink-0 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                      <p className={`text-sm ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                        {t('auth.login.twoFactorHint')}
                      </p>
                    </div>
                    <label className={`block text-sm font-medium mb-2 ${labelColor}`}>{t('auth.login.authCode')}</label>
                    <input
                      type="text"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className={`w-full px-4 py-3 rounded-xl border text-center text-xl font-mono tracking-[0.4em] focus:ring-2 focus:border-transparent transition-all ${inputClass}`}
                      placeholder="000000"
                      maxLength={6}
                      autoComplete="one-time-code"
                      autoFocus
                      disabled={loading}
                    />
                    <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={trustDevice}
                        onChange={(e) => setTrustDevice(e.target.checked)}
                        className="w-4 h-4 rounded border-white/20 text-amber-500 focus:ring-amber-500 bg-white/10"
                      />
                      <span className={`text-sm ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>
                        {t('auth.login.trustDevice')}
                      </span>
                    </label>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full flex items-center justify-center gap-2 rounded-xl py-3.5 px-4 font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${darkMode
                    ? 'border border-white/15 bg-white/[0.06] text-white hover:bg-white/[0.1]'
                    : 'bg-gray-900 text-white hover:bg-gray-800 shadow-lg'
                    }`}
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Building2 className="w-5 h-5" />}
                  {t('auth.login.submit')}
                </button>
              </form>

              <p className={`mt-6 text-center text-sm ${linkColor}`}>
                {t('auth.login.noAccount')}{' '}
                <Link to="/register" className={`font-semibold ${linkAccent}`}>
                  {t('auth.login.createAccountLink')}
                </Link>
              </p>
            </div>
          </section>
        </div>

        {/* ── Footer ── */}
        <div className="py-6 text-center">
          <p className={`text-sm ${footerColor}`}>
            &copy; {new Date().getFullYear()} {t('layout.footer.copyrightPrefix')} &middot; {t('layout.footer.tagline')}
          </p>
          <div className="mt-3 flex items-center justify-center gap-2.5">
            <img
              src="/gblogo.png"
              alt="Greybeard"
              className={`h-6 w-auto ${darkMode ? 'opacity-40' : 'opacity-30'}`}
            />
            <span className={`text-xs font-medium ${darkMode ? 'text-white/25' : 'text-gray-400'}`}>
              {t('auth.login.footerPoweredBy')}{' '}
              <span className={`font-semibold ${darkMode ? 'text-white/35' : 'text-gray-500'}`}>Greybeard</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
